import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import { statusColor } from "../format";
import type { ChargerConfig, CountedPage, Location } from "../types";
import { useToast } from "../toast";
import LocationDialog from "../dialogs/LocationDialog";

export default function LocationDetail({ id, onBack }: { id: number; onBack: () => void }) {
  const api = useAuthApi();
  const [location, setLocation] = useState<Location | null>(null);
  const [chargers, setChargers] = useState<ChargerConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [loc, cfgs] = await Promise.all([
        api<Location>(`${MGMT}/locations/${id}`),
        api<CountedPage<ChargerConfig>>(`${MGMT}/charger-configs`),
      ]);
      setLocation(loc);
      setChargers((cfgs?.items ?? []).filter((c) => c.location_id === id));
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}><CircularProgress /></Box>;
  if (!location) return <Typography color="error">Location not found</Typography>;

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton onClick={onBack}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" sx={{ flex: 1 }}>{location.name}</Typography>
        <Button startIcon={<EditIcon />} variant="outlined" onClick={() => setEditing(true)}>
          Edit
        </Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Address</Typography>
              <Stack spacing={0.5}>
                {location.address && <Typography>{location.address}</Typography>}
                <Typography>
                  {[location.city, location.country].filter(Boolean).join(", ") || "—"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Timezone: {location.timezone}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Coordinates</Typography>
              {location.latitude != null && location.longitude != null ? (
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    <strong>Lat:</strong> {location.latitude.toFixed(6)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Lon:</strong> {location.longitude.toFixed(6)}
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">No coordinates set</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Tax Profile</Typography>
              <Stack spacing={0.5}>
                <Typography variant="body2">
                  <strong>VAT / Tax rate:</strong> {location.vat_rate_percent.toFixed(2)}%
                </Typography>
                <Typography variant="body2">
                  <strong>Jurisdiction:</strong> {location.tax_jurisdiction || "—"}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Site Load Balancing
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">Mode</Typography>
                  <Typography sx={{ textTransform: "capitalize" }}>{location.load_balancing_mode ?? "dynamic"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">Site max power</Typography>
                  <Typography>{location.site_max_power_kw != null ? `${location.site_max_power_kw} kW` : "—"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">Peak window (UTC)</Typography>
                  <Typography>
                    {location.peak_start_hour != null && location.peak_end_hour != null
                      ? `${location.peak_start_hour}:00 - ${location.peak_end_hour}:00`
                      : "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">Peak max power</Typography>
                  <Typography>{location.peak_site_max_power_kw != null ? `${location.peak_site_max_power_kw} kW` : "—"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">Off-peak max power</Typography>
                  <Typography>{location.offpeak_site_max_power_kw != null ? `${location.offpeak_site_max_power_kw} kW` : "—"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary">Current solar production</Typography>
                  <Typography>{location.current_solar_production_kw != null ? `${location.current_solar_production_kw} kW` : "—"}</Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Smart Profiles
              </Typography>
              <Chip
                size="small"
                label={location.smart_charging_enabled ? "Enabled" : "Disabled"}
                color={location.smart_charging_enabled ? "success" : "default"}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Chargers at this location ({chargers.length})
          </Typography>
          {chargers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No chargers assigned</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Charge Point ID</TableCell>
                    <TableCell>Display Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Max Power</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chargers.map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell sx={{ fontFamily: "monospace" }}>{c.charge_point_id}</TableCell>
                      <TableCell>{c.display_name ?? "—"}</TableCell>
                      <TableCell>
                        <Chip label={c.status} size="small" color={statusColor(c.status)} />
                      </TableCell>
                      <TableCell align="right">
                        {c.max_power_kw != null ? `${c.max_power_kw} kW` : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <LocationDialog
        open={editing}
        location={location}
        onClose={() => setEditing(false)}
        onSaved={() => { setEditing(false); load(); }}
      />
    </>
  );
}
